import type { BuildPriority } from '@hangar/shared';
import { FormField, fieldA11y } from '@/components/FormField';
import { Input } from '@/components/ui/input';
import { OptionSelect } from '@/components/OptionSelect';
import { humanize } from '@/lib/format';
import { PRIORITIES, guardrailsFor } from './runBuildModel';
import type { RunBuildFormModel } from './useRunBuildForm';

/** Git branch (with its guardrails) and the optional commit SHA. */
export function RunBuildGitFields({ form }: { form: RunBuildFormModel }) {
  const { branch, commitSha } = form.fields.values;
  const { setBranch, setCommitSha } = form.fields.set;
  const branchGuardrails = guardrailsFor(form.guardrails, 'branch');
  const branchError = form.errorFor('branch', branch.length > 0);
  const commitError = form.errorFor('commitSha', true);
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <FormField id="run-build-branch" label="Git branch" required error={branchError ?? branchGuardrails.find((g) => g.level === 'block')?.message} hint={branchGuardrails[0]?.message}>
        <Input data-testid="run-build-branch" value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="master" className="font-mono sm:text-[13px]" spellCheck={false} autoComplete="off" {...fieldA11y('run-build-branch', branchError)} />
      </FormField>
      <FormField id="run-build-commit" label="Commit" hint="Optional · full SHA; empty = branch head" error={commitError}>
        <Input
          data-testid="run-build-commit"
          value={commitSha}
          onChange={(e) => setCommitSha(e.target.value.trim().toLowerCase())}
          placeholder="3f9c2b7e…"
          className="font-mono sm:text-[13px]"
          spellCheck={false}
          autoComplete="off"
          maxLength={40}
          {...fieldA11y('run-build-commit', commitError)}
        />
      </FormField>
    </div>
  );
}

/** Priority and the optional label of the build. */
export function RunBuildMetaFields({ form }: { form: RunBuildFormModel }) {
  const { priority, message } = form.fields.values;
  const { setPriority, setMessage } = form.fields.set;
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <FormField id="run-build-priority" label="Priority" hint="Higher priority builds start first when several wait">
        <OptionSelect id="run-build-priority" data-testid="run-build-priority" value={priority} onValueChange={(v) => setPriority(v as BuildPriority)} options={PRIORITIES.map((p) => ({ value: p, label: humanize(p) }))} />
      </FormField>
      <FormField id="run-build-message" label="Label" hint="Optional · shown with the build">
        <Input data-testid="run-build-message" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} placeholder="Release candidate 1.0.13" />
      </FormField>
    </div>
  );
}
